import React, { type ComponentType, useMemo } from 'react';
import { compose } from 'redux';
import memoizeOne from 'memoize-one';
import { graphql } from 'react-relay';
import { ErrorMessage } from '@atlaskit/form';
import { Stack } from '@atlaskit/primitives';
import type { ActionMeta } from '@atlassian/jira-common-components-picker/src/model';
import { ff } from '@atlassian/jira-feature-flagging';
import { PREFETCH_OPTIONS_COUNT } from '@atlassian/jira-issue-field-constants';
import {
	type MultiSelectEditViewProps,
	type MultiSelectEditViewDeprecatedProps,
	MultiSelectEditView,
} from '@atlassian/jira-issue-field-multi-select-editview-full';
import { useLazyLoadQueryAfterPageLoad } from '@atlassian/jira-issue-hooks/src/services/use-lazy-load-query-after-page-load';
import type { Option as SelectOption } from '@atlassian/jira-issue-internal-field-select';
import MultiSelectInlineEditView, {
	type Props as MultiSelectInlineEditViewProps,
} from '@atlassian/jira-issue-internal-field-select/src/multi-select-inline-edit';
import { genericMessages } from '@atlassian/jira-issue-view-common-constants/src/context-items-messages';
import getShowPinButton from '@atlassian/jira-issue-view-common-utils/src/get-show-pin-button';
import connectField from '@atlassian/jira-issue-view-common-views/src/connect-field/connect-field';
import { withExtraOwnProps } from '@atlassian/jira-issue-view-common-views/src/with-extra-own-props';
import { fieldARISelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/field-selector';
import {
	transformFromStateValue,
	transformToStateValue,
	transformRelayToReduxValue,
	transformReduxToRelayValue,
} from './transformer';

// TODO: Remove `MultiSelectEditViewDeprecatedProps` on `issue.details.follow-up-relay-multi-select-field-issue-view-integration` cleanup
type HandleOnChange = MultiSelectEditViewProps['onChange'] &
	MultiSelectEditViewDeprecatedProps['onChange'];

export const getCustomEditView = memoizeOne(
	(ari?: string) =>
		({
			onChange,
			value,
			noOptionsMessage,
			loadingMessage,
			invalidMessage,
			label,
			isInvalid,
			optionsFilter,
		}: MultiSelectInlineEditViewProps) => {
			const handleChange: HandleOnChange = (values, actionMeta) => {
				const legacyValues = values?.map(transformRelayToReduxValue) ?? [];
				const legacyActionMeta: ActionMeta<SelectOption> = {
					action: actionMeta.action,
					...(actionMeta.option
						? {
								option: transformRelayToReduxValue(actionMeta.option),
							}
						: null),
					...(actionMeta.removedValue
						? {
								removedValue: transformRelayToReduxValue(actionMeta.removedValue),
							}
						: null),
				};

				onChange?.(legacyValues, legacyActionMeta);
			};

			const selectedOptions = useMemo(() => value.map(transformReduxToRelayValue), [value]);

			const editViewNoOptionsMessage = useMemo(() => {
				if (noOptionsMessage) {
					return (inputValue: string) => noOptionsMessage(inputValue);
				}
			}, [noOptionsMessage]);

			const editViewLoadingMessage = useMemo(() => {
				if (loadingMessage) {
					return (inputValue: string) => loadingMessage(inputValue);
				}
			}, [loadingMessage]);

			const getCustomSelectableMessages = useMemo(() => {
				if (ff('issue.details.follow-up-relay-multi-select-field-issue-view-integration')) {
					return {
						noOptionsMessage,
						loadingMessage,
					};
				}

				return {};
			}, [noOptionsMessage, loadingMessage]);

			return (
				<Stack space="space.075">
					{Boolean(invalidMessage) && <ErrorMessage>{invalidMessage}</ErrorMessage>}
					<MultiSelectEditView
						autoFocus
						filterOptionsById={optionsFilter}
						fieldName={label}
						fieldId={ari || ''}
						value={selectedOptions}
						onChange={handleChange}
						noOptionsMessage={editViewNoOptionsMessage}
						loadingMessage={editViewLoadingMessage}
						isInvalid={isInvalid}
						{...getCustomSelectableMessages}
						isMulti
						spacing={
							ff('issue-view-fit-and-finish-full-page-milestone2_ebk0i') ? 'compact' : 'default'
						}
					/>
				</Stack>
			);
		},
);

// TODO: Remove `MultiSelectEditViewDeprecatedProps` on `issue.details.follow-up-relay-multi-select-field-issue-view-integration` cleanup
export type MultiSelectInlineEditViewWithPrefetchFieldOptionsProps = {
	ari: string;
} & MultiSelectInlineEditViewProps &
	MultiSelectEditViewDeprecatedProps;

export const MultiSelectInlineEditViewWithPrefetchFieldOptions = (
	props: MultiSelectInlineEditViewWithPrefetchFieldOptionsProps,
) => {
	// Temporary FF to disable prefetching for select customers that experience issue like https://getsupport.atlassian.com/browse/PCS-277464
	// Proper solution will be done under https://jplat.jira.atlassian.cloud/browse/JIV-18302
	if (ff('issue-view-disable-fields-prefetch') === false) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useLazyLoadQueryAfterPageLoad(
			graphql`
				query multiSelectWithRelayEditViewFieldOptionsPrefetchQuery(
					$id: ID!
					$first: Int
					$isSelectableField: Boolean!
				) {
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...multiSelect_issueFieldMultiSelectEditviewFull_MultiSelectEditViewWithFieldOptionsFragment
						@arguments(id: $id, first: $first, searchBy: "")
						@skip(if: $isSelectableField)
					# eslint-disable-next-line relay/must-colocate-fragment-spreads
					...ui_issueSelectableFieldEditView_SelectableFieldEditViewWithFieldOptionsFragment
						@arguments(id: $id, first: $first, searchBy: "")
						@include(if: $isSelectableField)
				}
			`,
			{
				id: props.ari,
				first: PREFETCH_OPTIONS_COUNT,
				isSelectableField: ff(
					'issue.details.follow-up-relay-multi-select-field-issue-view-integration',
				),
			},
		);
	}

	return <MultiSelectInlineEditView {...props} />;
};

export const MultiSelectFieldWithRelayEditView = compose<
	ComponentType<{ fieldId: string; area: string }>
>(
	withExtraOwnProps,
	connectField((_, ownPropsOnMount) => {
		const fieldARISelectorGetter = fieldARISelector(ownPropsOnMount.fieldId);

		return {
			fieldId: ownPropsOnMount.fieldId,
			transformFromStateValue,
			transformToStateValue,
			additionalProps: (state, intl) => ({
				noValueText: intl.formatMessage(genericMessages.noValue),
				showPinButton: getShowPinButton(ownPropsOnMount.area),
				customEditView: getCustomEditView(fieldARISelectorGetter(state)),
				ari: fieldARISelectorGetter(state),
			}),
		};
	}),
)(MultiSelectInlineEditViewWithPrefetchFieldOptions);
